// src/components/OffersModal/index.js - WITH AUTO-OFFER REMOVAL SUPPORT & BEAUTIFUL NOTIFICATIONS
import React, { useState, useEffect, useRef} from 'react';
import { X, Tag, Gift, TrendingDown, Percent, DollarSign, ShoppingCart, Package, UserPlus, AlertCircle, Zap, CheckCircle, XCircle } from 'lucide-react';
import './index.css';
import { settingsService } from '../../config/settingsService';

const OffersModal = ({ 
  show, 
  onClose, 
  offersManager,
  cart,
  getBranchId,
  getCustomerId,
  couponCode,
  onCouponChange,
  onApplyCoupon,
  activeOffers = []
}) => {
  const [activeTab, setActiveTab] = useState('applied');
  const [processing, setProcessing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [countdown, setCountdown] = useState(3);

  // ✅ Auto-dismiss notification after 6 seconds (increased from 4)
  useEffect(() => {
    if (!notification) return;

    setCountdown(3);

    const interval = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [notification]);

  useEffect(() => {
    if (countdown === 0 && notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 800); // allow UI to render "Closing in 1s"

      return () => clearTimeout(timer);
    }
  }, [countdown, notification]);

  // ✅ Show beautiful notification instead of alert
  const showNotification = (type, title, message, offerDetails = null) => {
    setNotification({
      type, // 'success', 'error', 'info'
      title,
      message,
      offerDetails
    });
  };

  // ✅ Detect when auto-offers are applied - FIXED VERSION
  const prevAutoOffersRef = useRef([]);

  useEffect(() => {
    if (!offersManager) return; // ✅ REMOVED: !show check
    
    const unsubscribe = offersManager.subscribe((state) => {
      const autoOffers = state.autoAppliedOffers || [];
      const prevAutoOffers = prevAutoOffersRef.current;
      
      // Check if there are NEW auto-offers (not previously shown)
      if (autoOffers.length > prevAutoOffers.length) {
        // Find the newly added offer
        const newOffers = autoOffers.filter(
          offer => !prevAutoOffers.some(prev => prev.id === offer.id)
        );
        
        // Show notification for each new auto-offer
        newOffers.forEach(offer => {
          showNotification(
            'success',
            '⚡ Auto-Applied!',
            `${offer.name}`,
            {
              name: offer.name,
              discount: offer.discount_amount || 0,
              type: offer.type
            }
          );
        });
      }
      
      // Update the reference
      prevAutoOffersRef.current = autoOffers;
    });
    
    return () => unsubscribe();
  }, [offersManager]); // ✅ REMOVED: 'show' from dependencies

  // Get offers from manager
  const appliedOffers = offersManager?.getAllAppliedOffers() || [];
  const availableOffers = offersManager?.availableOffers || [];
  
  // ✅ Separate auto-applied and manual offers for display
  const autoAppliedOffers = offersManager?.autoAppliedOffers || [];
  const manuallyAppliedOffers = offersManager?.appliedOffers || [];
  
  // Check if any offer is non-stackable
  const hasNonStackable = appliedOffers.some(o => !o.stackable);

  const getOfferIcon = (type) => {
    switch(type) {
      case 'percentage': return <Percent size={20} />;
      case 'fixed_amount': return <DollarSign size={20} />;
      case 'minimum_order': return <ShoppingCart size={20} />;
      case 'bogo': return <Gift size={20} />;
      case 'buy_x_get_y': return <Gift size={20} />;
      case 'free_item': return <Gift size={20} />;
      case 'combo': return <Package size={20} />;
      case 'category_discount': return <Tag size={20} />;
      case 'first_order': return <UserPlus size={20} />;
      case 'tiered_discount': return <TrendingDown size={20} />;
      default: return <Tag size={20} />;
    }
  };

  const getOfferColor = (type) => {
    const rootStyle = getComputedStyle(document.documentElement);
    switch(type) {
      case 'percentage': return rootStyle.getPropertyValue('--color-info').trim() || '#3b82f6';
      case 'fixed_amount': return rootStyle.getPropertyValue('--color-success').trim() || '#10b981';
      case 'minimum_order': return rootStyle.getPropertyValue('--color-accent-alt').trim() || '#8b5cf6';
      case 'bogo': return rootStyle.getPropertyValue('--color-warning').trim() || '#f59e0b';
      case 'buy_x_get_y': return '#ec4899';
      case 'free_item': return '#14b8a6';
      case 'combo': return '#6366f1';
      case 'category_discount': return rootStyle.getPropertyValue('--color-warning').trim() || '#f59e0b';
      case 'first_order': return rootStyle.getPropertyValue('--color-error').trim() || '#ef4444';
      case 'tiered_discount': return rootStyle.getPropertyValue('--color-accent-alt').trim() || '#8b5cf6';
      default: return rootStyle.getPropertyValue('--color-text-light').trim() || '#6b7280';
    }
  };

  const getOfferTypeName = (type) => {
    switch(type) {
      case 'percentage': return 'Percentage Off';
      case 'fixed_amount': return 'Fixed Discount';
      case 'minimum_order': return 'Min Order Discount';
      case 'bogo': return 'Buy 1 Get 1';
      case 'buy_x_get_y': return 'Buy X Get Y';
      case 'free_item': return 'Free Item';
      case 'combo': return 'Combo Deal';
      case 'category_discount': return 'Category Discount';
      case 'first_order': return 'Welcome Offer';
      case 'tiered_discount': return 'Tiered Discount';
      default: return 'Special Offer';
    }
  };

  const handleApplyOffer = async (offer) => {
    if (processing) return;
    
    setProcessing(true);
    
    try {
      if (!cart || cart.length === 0) {
        showNotification('error', '❌ Cart Empty', 'Please add items to your cart before applying an offer');
        setProcessing(false);
        return;
      }

      if (typeof getBranchId !== 'function' || typeof getCustomerId !== 'function') {
        console.error('❌ Missing required functions:', { getBranchId, getCustomerId });
        showNotification('error', '❌ Error', 'Missing required information');
        setProcessing(false);
        return;
      }

      const branchId = getBranchId();
      const customerId = getCustomerId();

      console.log('🎯 Applying offer:', {
        offerId: offer.id,
        offerName: offer.name,
        cartItems: cart.length,
        branchId,
        customerId
      });

      const result = await offersManager.applyOffer(
        offer,
        cart,
        branchId,
        customerId
      );
      
      if (result.success) {
        showNotification(
          'success', 
          '✅ Offer Applied!', 
          `${offer.name}`,
          {
            name: offer.name,
            discount: offer.discount_amount,
            type: offer.type
          }
        );
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        showNotification('error', '❌ Offer Not Applied', result.message);
      }
    } catch (error) {
      console.error('Error applying offer:', error);
      showNotification('error', '❌ Error', `Failed to apply offer: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveOffer = async (offerId, isAutoApplied = false) => {
    if (processing) return;
    
    setProcessing(true);
    
    try {
      if (typeof getBranchId !== 'function' || typeof getCustomerId !== 'function') {
        console.error('❌ Missing required functions:', { getBranchId, getCustomerId });
        showNotification('error', '❌ Error', 'Missing required information');
        setProcessing(false);
        return;
      }

      const branchId = getBranchId();
      const customerId = getCustomerId();

      // ✅ Show different confirmation for auto-applied offers
      if (isAutoApplied) {
        const confirm = window.confirm(
          'This is an automatically applied offer.\n\n' +
          'Removing it will prevent it from being auto-applied again.\n\n' +
          'Do you want to continue?'
        );
        
        if (!confirm) {
          setProcessing(false);
          return;
        }
      }

      console.log('🗑️ Removing offer:', offerId);

      // Find the offer being removed BEFORE removing it
      const offerToRemove = appliedOffers.find(o => o.id === offerId);

      const result = await offersManager.removeOffer(
        offerId,
        cart,
        branchId,
        customerId
      );

      if (result.success) {
        showNotification(
          'success', 
          '✅ Offer Removed', 
          offerToRemove ? offerToRemove.name : 'Offer removed successfully',
          offerToRemove ? {
            name: offerToRemove.name,
            discount: offerToRemove.discount_amount || 0,
            type: offerToRemove.type
          } : null
        );
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        showNotification('error', '❌ Error', result.message);
      }
    } catch (error) {
      console.error('Error removing offer:', error);
      showNotification('error', '❌ Error', `Failed to remove offer: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const renderOfferCard = (offer, isApplied = false, isAutoApplied = false) => {
    const color = getOfferColor(offer.type);
    const canApply = !hasNonStackable || isApplied;
    
    return (
      <div 
        key={offer.id} 
        className="offer-card" 
        style={{ 
          borderColor: isApplied ? '#10b981' : '#e5e7eb',
          opacity: (!isApplied && hasNonStackable) ? 0.6 : 1,
          backgroundColor: isAutoApplied ? '#f0fdf4' : 'white'
        }}
      >
        <div className="offer-card-header">
          <div className="offer-icon" style={{ backgroundColor: `${color}20`, color: color }}>
            {getOfferIcon(offer.type)}
          </div>
          <div className="offer-info">
            <h4 className="offer-name">{offer.name}</h4>
            <span className="offer-type-badge" style={{ backgroundColor: `${color}20`, color: color }}>
              {getOfferTypeName(offer.type)}
            </span>
            {!offer.stackable && (
              <span 
                className="non-stackable-badge" 
                title="This offer cannot be combined with others"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 8px',
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  marginTop: '4px'
                }}
              >
                <AlertCircle size={14} />
                Non-stackable
              </span>
            )}
          </div>
          {isApplied && (
            <span 
              className="applied-badge"
              style={{
                backgroundColor: isAutoApplied ? '#10b981' : '#3b82f6',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {isAutoApplied && <Zap size={14} />}
              {isAutoApplied ? 'Auto-Applied' : 'Applied'}
            </span>
          )}
        </div>

        {offer.display_text && (
          <div className="offer-display-text" style={{ backgroundColor: `${color}10`, color: color }}>
            {offer.display_text}
          </div>
        )}

        <div className="offer-details">
          {offer.discount_amount > 0 && (
            <div className="offer-detail-row">
              <span className="detail-label">
                <TrendingDown size={16} />
                Discount
              </span>
              <span className="detail-value">{settingsService.formatPrice(offer.discount_amount)}</span>
            </div>
          )}

          {offer.free_items && offer.free_items.length > 0 && (
            <div className="offer-detail-row">
              <span className="detail-label">
                <Gift size={16} />
                Free Items
              </span>
              <span className="detail-value">{offer.free_items.length} item(s)</span>
            </div>
          )}
        </div>

        {offer.message && (
          <div className="offer-message">
            <div className="offer-message-icon">ℹ️</div>
            <p>{offer.message}</p>
          </div>
        )}

        {isAutoApplied && (
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '8px',
            marginBottom: '8px',
            fontSize: '13px',
            color: '#065f46',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <Zap size={14} />
            <span>Automatically applied based on your cart</span>
          </div>
        )}

        {!isApplied && hasNonStackable && (
          <div className="offer-warning" style={{ 
            backgroundColor: '#fef3c7', 
            padding: '8px 12px', 
            borderRadius: '8px',
            marginBottom: '8px',
            fontSize: '13px',
            color: '#92400e'
          }}>
            ⚠️ Remove existing offer to apply this one
          </div>
        )}

        {isApplied ? (
          <button 
            className="offer-remove-btn"
            onClick={() => handleRemoveOffer(offer.id, isAutoApplied)}
            disabled={processing}
            style={{
              opacity: processing ? 0.6 : 1,
              cursor: processing ? 'not-allowed' : 'pointer',
              backgroundColor: isAutoApplied ? '#dc2626' : '#ef4444'
            }}
          >
            {processing ? 'Removing...' : isAutoApplied ? 'Remove Auto Offer' : 'Remove Offer'}
          </button>
        ) : (
          <button 
            className="offer-apply-btn"
            onClick={() => handleApplyOffer(offer)}
            disabled={processing || (!canApply && !offer.stackable)}
            style={{ 
              backgroundColor: color,
              opacity: (processing || (!canApply && !offer.stackable)) ? 0.6 : 1,
              cursor: (processing || (!canApply && !offer.stackable)) ? 'not-allowed' : 'pointer'
            }}
          >
            {processing ? 'Applying...' : 'Apply Offer'}
          </button>
        )}
      </div>
    );
  };

  // ✅ FIXED: Notification OUTSIDE modal, always visible
  return (
    <>
      {/* Notification - Shows even when modal is closed */}
      {notification && (
        <div className="offer-notification-overlay" onClick={() => setNotification(null)}>
          <div className="offer-notification-container" onClick={(e) => e.stopPropagation()}>
            {notification.type === 'success' && (
              <div className="offer-notification-success-circle">
                <CheckCircle size={48} color="#16a34a" />
              </div>
            )}
            
            {notification.type === 'error' && (
              <div className="offer-notification-error-circle">
                <XCircle size={48} color="#ef4444" />
              </div>
            )}

            <h2 className="offer-notification-title">{notification.title}</h2>

            {notification.offerDetails && (
              <div className="offer-notification-details-box">
                <span className="offer-notification-label">You Saved:</span>
                <span className="offer-notification-value">
                  {settingsService.formatPrice(notification.offerDetails.discount)}
                </span>
              </div>
            )}

            <p className="offer-notification-subtitle">{notification.message}</p>

            <div className="offer-notification-countdown">
              Closing in {countdown}s
            </div>
          </div>
        </div>
      )}

      {/* Modal - Only shows when 'show' prop is true */}
      {show && (
        <div className="offers-modal-overlay" onClick={onClose}>
          <div className="offers-modal" onClick={(e) => e.stopPropagation()}>
            <div className="offers-modal-header">
              <div className="offers-modal-title-container">
                <Tag size={24} />
                <h2>Offers & Coupons</h2>
              </div>
              <button onClick={onClose} className="offers-modal-close">
                <X size={24} />
              </button>
            </div>

            <div className="coupon-input-section">
              <div className="coupon-input-wrapper">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => onCouponChange(e.target.value)}
                  className="coupon-input"
                  disabled={processing}
                />
                <button 
                  onClick={onApplyCoupon}
                  className="coupon-apply-btn"
                  disabled={!couponCode.trim() || processing}
                >
                  {processing ? 'Applying...' : 'Apply'}
                </button>
              </div>
            </div>

            <div className="offers-tabs">
              <button 
                className={`offers-tab ${activeTab === 'applied' ? 'active' : ''}`}
                onClick={() => setActiveTab('applied')}
              >
                Applied ({appliedOffers.length})
              </button>
              <button 
                className={`offers-tab ${activeTab === 'available' ? 'active' : ''}`}
                onClick={() => setActiveTab('available')}
              >
                Available ({availableOffers.length})
              </button>
              <button 
                className={`offers-tab ${activeTab === 'active' ? 'active' : ''}`}
                onClick={() => setActiveTab('active')}
              >
                All Offers ({activeOffers.length})
              </button>
            </div>

            <div className="offers-modal-content">
              {activeTab === 'applied' && (
                <div className="offers-section">
                  {appliedOffers.length === 0 ? (
                    <div className="empty-state">
                      <Tag size={48} color="#ccc" />
                      <p>No offers applied yet</p>
                      <span>Browse available offers below</span>
                    </div>
                  ) : (
                    <>
                      <div className="section-header">
                        <h3>✅ Applied Offers ({appliedOffers.length})</h3>
                        <span className="total-savings">
                          Total Savings: {settingsService.formatPrice(offersManager.getTotalSavings())}
                        </span>
                      </div>
                      {hasNonStackable && (
                        <div style={{
                          padding: '5px',
                          backgroundColor: '#fef3c7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#92400e'
                        }}>
                          <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
                          You have a non-stackable offer applied. Remove it to apply other offers.
                        </div>
                      )}
                      
                      {autoAppliedOffers.length > 0 && (
                        <>
                          <div style={{ 
                            marginBottom: '12px', 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            <Zap size={16} />
                            Auto-Applied Offers ({autoAppliedOffers.length})
                          </div>
                          <div className="offers-grid">
                            {autoAppliedOffers.map(offer => renderOfferCard(offer, true, true))}
                          </div>
                        </>
                      )}
                      
                      {manuallyAppliedOffers.length > 0 && (
                        <>
                          <div style={{ 
                            marginTop: autoAppliedOffers.length > 0 ? '24px' : '0',
                            marginBottom: '12px', 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: '#3b82f6'
                          }}>
                            Manually Applied Offers ({manuallyAppliedOffers.length})
                          </div>
                          <div className="offers-grid">
                            {manuallyAppliedOffers.map(offer => renderOfferCard(offer, true, false))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'available' && (
                <div className="offers-section">
                  {availableOffers.length > 0 ? (
                    <>
                      <div className="section-header">
                        <h3>🎯 Available Offers</h3>
                      </div>
                      <div className="offers-grid">
                        {availableOffers.map(offer => renderOfferCard(offer))}
                      </div>
                    </>
                  ) : (
                    <div className="empty-state">
                      <Gift size={48} color="#ccc" />
                      <p>No offers available for your cart</p>
                      <span>Add more items to unlock offers</span>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'active' && (
                <div className="offers-section">
                  {activeOffers.length === 0 ? (
                    <div className="empty-state">
                      <Tag size={48} color="#ccc" />
                      <p>No active offers</p>
                      <span>Check back later for new offers</span>
                    </div>
                  ) : (
                    <>
                      <div className="section-header">
                        <h3>🎉 All Active Offers</h3>
                      </div>
                      <div className="offers-grid">
                        {activeOffers.map(offer => (
                          <div key={offer.id} className="offer-card">
                            <div className="offer-card-header">
                              <div className="offer-icon" style={{ backgroundColor: '#f59e0b20', color: '#f59e0b' }}>
                                <Tag size={20} />
                              </div>
                              <div className="offer-info">
                                <h4 className="offer-name">{offer.name}</h4>
                                <p className="offer-description">{offer.description}</p>
                              </div>
                            </div>

                            {offer.coupon_code && (
                              <div className="offer-code">
                                <span>Code: <strong>{offer.coupon_code}</strong></span>
                                <button 
                                  className="copy-code-btn"
                                  onClick={() => {
                                    navigator.clipboard.writeText(offer.coupon_code);
                                    onCouponChange(offer.coupon_code);
                                  }}
                                >
                                  Copy
                                </button>
                              </div>
                            )}

                            <button 
                              className="offer-apply-btn"
                              onClick={() => {
                                if (offer.coupon_code) {
                                  onCouponChange(offer.coupon_code);
                                  setTimeout(() => onApplyCoupon(), 100);
                                }
                              }}
                              disabled={processing}
                            >
                              Apply This Offer
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OffersModal;



// // src/components/OffersModal/index.js - WITH AUTO-OFFER REMOVAL SUPPORT & BEAUTIFUL NOTIFICATIONS
// import React, { useState, useEffect, useRef} from 'react';
// import { X, Tag, Gift, TrendingDown, Percent, DollarSign, ShoppingCart, Package, UserPlus, AlertCircle, Zap, CheckCircle, XCircle } from 'lucide-react';
// import './index.css';

// const OffersModal = ({ 
//   show, 
//   onClose, 
//   offersManager,
//   cart,
//   getBranchId,
//   getCustomerId,
//   couponCode,
//   onCouponChange,
//   onApplyCoupon,
//   activeOffers = []
// }) => {
//   const [activeTab, setActiveTab] = useState('applied');
//   const [processing, setProcessing] = useState(false);
//   const [notification, setNotification] = useState(null);
// const [countdown, setCountdown] = useState(3);

//   // ✅ Auto-dismiss notification after 4 seconds
//  // ✅ Auto-dismiss notification after 6 seconds (increased from 4)
// useEffect(() => {
//   if (!notification) return;

// setCountdown(3);

//   const interval = setInterval(() => {
//     setCountdown(prev => prev - 1);
//   }, 1000);

//   return () => clearInterval(interval);
// }, [notification]);
// useEffect(() => {
//   if (countdown === 0 && notification) {
//     const timer = setTimeout(() => {
//       setNotification(null);
//     }, 800); // allow UI to render "Closing in 1s"

//     return () => clearTimeout(timer);
//   }
// }, [countdown, notification]);



//   // ✅ Show beautiful notification instead of alert
//   const showNotification = (type, title, message, offerDetails = null) => {
//     setNotification({
//       type, // 'success', 'error', 'info'
//       title,
//       message,
//       offerDetails
//     });
//   };

//   // ✅ Detect when auto-offers are applied
// // ✅ Detect when auto-offers are applied - FIXED VERSION
// const prevAutoOffersRef = useRef([]);

// useEffect(() => {
//   if (!offersManager || !show) return; // Only run when modal is open
  
//   const unsubscribe = offersManager.subscribe((state) => {
//     const autoOffers = state.autoAppliedOffers || [];
//     const prevAutoOffers = prevAutoOffersRef.current;
    
//     // Check if there are NEW auto-offers (not previously shown)
//     if (autoOffers.length > prevAutoOffers.length) {
//       // Find the newly added offer
//       const newOffers = autoOffers.filter(
//         offer => !prevAutoOffers.some(prev => prev.id === offer.id)
//       );
      
//       // Show notification for each new auto-offer
//       newOffers.forEach(offer => {
//         showNotification(
//           'success',
//           '⚡ Auto-Applied!',
//           `${offer.name}`,
//           {
//             name: offer.name,
//             discount: offer.discount_amount || 0,
//             type: offer.type
//           }
//         );
//       });
//     }
    
//     // Update the reference
//     prevAutoOffersRef.current = autoOffers;
//   });
  
//   return () => unsubscribe();
// }, [offersManager, show]); // Added 'show' dependency
//   if (!show) return null;

//   // Get offers from manager
//   const appliedOffers = offersManager?.getAllAppliedOffers() || [];
//   const availableOffers = offersManager?.availableOffers || [];
  
//   // ✅ Separate auto-applied and manual offers for display
//   const autoAppliedOffers = offersManager?.autoAppliedOffers || [];
//   const manuallyAppliedOffers = offersManager?.appliedOffers || [];
  
//   // Check if any offer is non-stackable
//   const hasNonStackable = appliedOffers.some(o => !o.stackable);

//   const getOfferIcon = (type) => {
//     switch(type) {
//       case 'percentage': return <Percent size={20} />;
//       case 'fixed_amount': return <DollarSign size={20} />;
//       case 'minimum_order': return <ShoppingCart size={20} />;
//       case 'bogo': return <Gift size={20} />;
//       case 'buy_x_get_y': return <Gift size={20} />;
//       case 'free_item': return <Gift size={20} />;
//       case 'combo': return <Package size={20} />;
//       case 'category_discount': return <Tag size={20} />;
//       case 'first_order': return <UserPlus size={20} />;
//       case 'tiered_discount': return <TrendingDown size={20} />; // ✅ NEW
//       default: return <Tag size={20} />;
//     }
//   };

//   const getOfferColor = (type) => {
//     const rootStyle = getComputedStyle(document.documentElement);
//     switch(type) {
//       case 'percentage': return rootStyle.getPropertyValue('--color-info').trim() || '#3b82f6';
//       case 'fixed_amount': return rootStyle.getPropertyValue('--color-success').trim() || '#10b981';
//       case 'minimum_order': return rootStyle.getPropertyValue('--color-accent-alt').trim() || '#8b5cf6';
//       case 'bogo': return rootStyle.getPropertyValue('--color-warning').trim() || '#f59e0b';
//       case 'buy_x_get_y': return '#ec4899';
//       case 'free_item': return '#14b8a6';
//       case 'combo': return '#6366f1';
//       case 'category_discount': return rootStyle.getPropertyValue('--color-warning').trim() || '#f59e0b';
//       case 'first_order': return rootStyle.getPropertyValue('--color-error').trim() || '#ef4444';
//       case 'tiered_discount': return rootStyle.getPropertyValue('--color-accent-alt').trim() || '#8b5cf6';
//       default: return rootStyle.getPropertyValue('--color-text-light').trim() || '#6b7280';
//     }
//   };

//   const getOfferTypeName = (type) => {
//     switch(type) {
//       case 'percentage': return 'Percentage Off';
//       case 'fixed_amount': return 'Fixed Discount';
//       case 'minimum_order': return 'Min Order Discount';
//       case 'bogo': return 'Buy 1 Get 1';
//       case 'buy_x_get_y': return 'Buy X Get Y';
//       case 'free_item': return 'Free Item';
//       case 'combo': return 'Combo Deal';
//       case 'category_discount': return 'Category Discount';
//       case 'first_order': return 'Welcome Offer';
//       case 'tiered_discount': return 'Tiered Discount'; // ✅ NEW
//       default: return 'Special Offer';
//     }
//   };

//   const handleApplyOffer = async (offer) => {
//     if (processing) return;
    
//     setProcessing(true);
    
//     try {
//       if (!cart || cart.length === 0) {
//         showNotification('error', '❌ Cart Empty', 'Please add items to your cart before applying an offer');
//         setProcessing(false);
//         return;
//       }

//       if (typeof getBranchId !== 'function' || typeof getCustomerId !== 'function') {
//         console.error('❌ Missing required functions:', { getBranchId, getCustomerId });
//         showNotification('error', '❌ Error', 'Missing required information');
//         setProcessing(false);
//         return;
//       }

//       const branchId = getBranchId();
//       const customerId = getCustomerId();

//       console.log('🎯 Applying offer:', {
//         offerId: offer.id,
//         offerName: offer.name,
//         cartItems: cart.length,
//         branchId,
//         customerId
//       });

//       const result = await offersManager.applyOffer(
//         offer,
//         cart,
//         branchId,
//         customerId
//       );
      
//       if (result.success) {
//         showNotification(
//           'success', 
//           '✅ Offer Applied!', 
//           `${offer.name}`,
//           {
//             name: offer.name,
//             discount: offer.discount_amount,
//             type: offer.type
//           }
//         );
//          setTimeout(() => {
//     onClose();
//   }, 3000);
    

        
//       } else {
//         showNotification('error', '❌ Offer Not Applied', result.message);
//       }
//     } catch (error) {
//       console.error('Error applying offer:', error);
//       showNotification('error', '❌ Error', `Failed to apply offer: ${error.message}`);
//     } finally {
//       setProcessing(false);
//     }
//   };

//   const handleRemoveOffer = async (offerId, isAutoApplied = false) => {
//     if (processing) return;
    
//     setProcessing(true);
    
//     try {
//       if (typeof getBranchId !== 'function' || typeof getCustomerId !== 'function') {
//         console.error('❌ Missing required functions:', { getBranchId, getCustomerId });
//         showNotification('error', '❌ Error', 'Missing required information');
//         setProcessing(false);
//         return;
//       }

//       const branchId = getBranchId();
//       const customerId = getCustomerId();

//       // ✅ Show different confirmation for auto-applied offers
//       if (isAutoApplied) {
//         const confirm = window.confirm(
//           'This is an automatically applied offer.\n\n' +
//           'Removing it will prevent it from being auto-applied again.\n\n' +
//           'Do you want to continue?'
//         );
        
//         if (!confirm) {
//           setProcessing(false);
//           return;
//         }
//       }

//       console.log('🗑️ Removing offer:', offerId);

//       // Find the offer being removed BEFORE removing it
// const offerToRemove = appliedOffers.find(o => o.id === offerId);

// const result = await offersManager.removeOffer(
//   offerId,
//   cart,
//   branchId,
//   customerId
// );

// if (result.success) {
//   showNotification(
//     'success', 
//     '✅ Offer Removed', 
//     offerToRemove ? offerToRemove.name : 'Offer removed successfully',
//     offerToRemove ? {
//       name: offerToRemove.name,
//       discount: offerToRemove.discount_amount || 0,
//       type: offerToRemove.type
//     } : null
//   );
//   setTimeout(() => {
//     onClose();
//   }, 3000);
 
// } else {
//   showNotification('error', '❌ Error', result.message);
// }
//     } catch (error) {
//       console.error('Error removing offer:', error);
//       showNotification('error', '❌ Error', `Failed to remove offer: ${error.message}`);
//     } finally {
//       setProcessing(false);
//     }
//   };

//   /**
//    * ✅ UPDATED: Render offer card with auto-applied badge
//    */
//   const renderOfferCard = (offer, isApplied = false, isAutoApplied = false) => {
//     const color = getOfferColor(offer.type);
//     const canApply = !hasNonStackable || isApplied;
    
//     return (
//       <div 
//         key={offer.id} 
//         className="offer-card" 
//         style={{ 
//           borderColor: isApplied ? '#10b981' : '#e5e7eb',
//           opacity: (!isApplied && hasNonStackable) ? 0.6 : 1,
//           backgroundColor: isAutoApplied ? '#f0fdf4' : 'white' // ✅ Light green background for auto
//         }}
//       >
//         <div className="offer-card-header">
//           <div className="offer-icon" style={{ backgroundColor: `${color}20`, color: color }}>
//             {getOfferIcon(offer.type)}
//           </div>
//           <div className="offer-info">
//             <h4 className="offer-name">{offer.name}</h4>
//             <span className="offer-type-badge" style={{ backgroundColor: `${color}20`, color: color }}>
//               {getOfferTypeName(offer.type)}
//             </span>
//             {!offer.stackable && (
//               <span 
//                 className="non-stackable-badge" 
//                 title="This offer cannot be combined with others"
//                 style={{
//                   display: 'inline-flex',
//                   alignItems: 'center',
//                   gap: '4px',
//                   padding: '2px 8px',
//                   backgroundColor: '#fef3c7',
//                   color: '#92400e',
//                   borderRadius: '4px',
//                   fontSize: '12px',
//                   fontWeight: '500',
//                   marginTop: '4px'
//                 }}
//               >
//                 <AlertCircle size={14} />
//                 Non-stackable
//               </span>
//             )}
//           </div>
//           {/* ✅ Show different badge for auto vs manual */}
//           {isApplied && (
//             <span 
//               className="applied-badge"
//               style={{
//                 backgroundColor: isAutoApplied ? '#10b981' : '#3b82f6',
//                 color: 'white',
//                 padding: '4px 12px',
//                 borderRadius: '12px',
//                 fontSize: '12px',
//                 fontWeight: '600',
//                 display: 'flex',
//                 alignItems: 'center',
//                 gap: '4px'
//               }}
//             >
//               {isAutoApplied && <Zap size={14} />}
//               {isAutoApplied ? 'Auto-Applied' : 'Applied'}
//             </span>
//           )}
//         </div>

//         {offer.display_text && (
//           <div className="offer-display-text" style={{ backgroundColor: `${color}10`, color: color }}>
//             {offer.display_text}
//           </div>
//         )}

//         <div className="offer-details">
//           {offer.discount_amount > 0 && (
//             <div className="offer-detail-row">
//               <span className="detail-label">
//                 <TrendingDown size={16} />
//                 Discount
//               </span>
//               <span className="detail-value">₹{offer.discount_amount.toFixed(2)}</span>
//             </div>
//           )}

//           {offer.free_items && offer.free_items.length > 0 && (
//             <div className="offer-detail-row">
//               <span className="detail-label">
//                 <Gift size={16} />
//                 Free Items
//               </span>
//               <span className="detail-value">{offer.free_items.length} item(s)</span>
//             </div>
//           )}
//         </div>

//         {offer.message && (
//           <div className="offer-message">
//             <div className="offer-message-icon">ℹ️</div>
//             <p>{offer.message}</p>
//           </div>
//         )}

//         {/* ✅ Show info for auto-applied offers */}
//         {isAutoApplied && (
//           <div style={{
//             padding: '8px 12px',
//             backgroundColor: '#d1fae5',
//             borderRadius: '8px',
//             marginBottom: '8px',
//             fontSize: '13px',
//             color: '#065f46',
//             display: 'flex',
//             alignItems: 'center',
//             gap: '6px'
//           }}>
//             <Zap size={14} />
//             <span>Automatically applied based on your cart</span>
//           </div>
//         )}

//         {/* Stackability warning */}
//         {!isApplied && hasNonStackable && (
//           <div className="offer-warning" style={{ 
//             backgroundColor: '#fef3c7', 
//             padding: '8px 12px', 
//             borderRadius: '8px',
//             marginBottom: '8px',
//             fontSize: '13px',
//             color: '#92400e'
//           }}>
//             ⚠️ Remove existing offer to apply this one
//           </div>
//         )}

//         {/* Action buttons */}
//         {isApplied ? (
//           <button 
//             className="offer-remove-btn"
//             onClick={() => handleRemoveOffer(offer.id, isAutoApplied)}
//             disabled={processing}
//             style={{
//               opacity: processing ? 0.6 : 1,
//               cursor: processing ? 'not-allowed' : 'pointer',
//               backgroundColor: isAutoApplied ? '#dc2626' : '#ef4444'
//             }}
//           >
//             {processing ? 'Removing...' : isAutoApplied ? 'Remove Auto Offer' : 'Remove Offer'}
//           </button>
//         ) : (
//           <button 
//             className="offer-apply-btn"
//             onClick={() => handleApplyOffer(offer)}
//             disabled={processing || (!canApply && !offer.stackable)}
//             style={{ 
//               backgroundColor: color,
//               opacity: (processing || (!canApply && !offer.stackable)) ? 0.6 : 1,
//               cursor: (processing || (!canApply && !offer.stackable)) ? 'not-allowed' : 'pointer'
//             }}
//           >
//             {processing ? 'Applying...' : 'Apply Offer'}
//           </button>
//         )}
//       </div>
//     );
//   };

//   return (
//     <div className="offers-modal-overlay" onClick={onClose}>
//       <div className="offers-modal" onClick={(e) => e.stopPropagation()}>
//         <div className="offers-modal-header">
//           <div className="offers-modal-title-container">
//             <Tag size={24} />
//             <h2>Offers & Coupons</h2>
//           </div>
//           <button onClick={onClose} className="offers-modal-close">
//             <X size={24} />
//           </button>
//         </div>

//         {/* Coupon Input Section */}
//         <div className="coupon-input-section">
//           <div className="coupon-input-wrapper">
//             <input
//               type="text"
//               placeholder="Enter coupon code"
//               value={couponCode}
//               onChange={(e) => onCouponChange(e.target.value)}
//               className="coupon-input"
//               disabled={processing}
//             />
//             <button 
//               onClick={onApplyCoupon}
//               className="coupon-apply-btn"
//               disabled={!couponCode.trim() || processing}
//             >
//               {processing ? 'Applying...' : 'Apply'}
//             </button>
//           </div>
//         </div>

//         {/* Tabs */}
//         <div className="offers-tabs">
//           <button 
//             className={`offers-tab ${activeTab === 'applied' ? 'active' : ''}`}
//             onClick={() => setActiveTab('applied')}
//           >
//             Applied ({appliedOffers.length})
//           </button>
//           <button 
//             className={`offers-tab ${activeTab === 'available' ? 'active' : ''}`}
//             onClick={() => setActiveTab('available')}
//           >
//             Available ({availableOffers.length})
//           </button>
//           <button 
//             className={`offers-tab ${activeTab === 'active' ? 'active' : ''}`}
//             onClick={() => setActiveTab('active')}
//           >
//             All Offers ({activeOffers.length})
//           </button>
//         </div>

//         <div className="offers-modal-content">
//           {/* Applied Offers Tab */}
//           {activeTab === 'applied' && (
//             <div className="offers-section">
//               {appliedOffers.length === 0 ? (
//                 <div className="empty-state">
//                   <Tag size={48} color="#ccc" />
//                   <p>No offers applied yet</p>
//                   <span>Browse available offers below</span>
//                 </div>
//               ) : (
//                 <>
//                   <div className="section-header">
//                     <h3>✅ Applied Offers ({appliedOffers.length})</h3>
//                     <span className="total-savings">
//                       Total Savings: ₹{offersManager.getTotalSavings().toFixed(2)}
//                     </span>
//                   </div>
//                   {hasNonStackable && (
//                     <div style={{
//                       padding: '5px',
//                       backgroundColor: '#fef3c7',
//                       borderRadius: '8px',
//                       fontSize: '14px',
//                       color: '#92400e'
//                     }}>
//                       <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
//                       You have a non-stackable offer applied. Remove it to apply other offers.
//                     </div>
//                   )}
                  
//                   {/* ✅ Show auto-applied offers separately */}
//                   {autoAppliedOffers.length > 0 && (
//                     <>
//                       <div style={{ 
//                         marginBottom: '12px', 
//                         fontSize: '14px', 
//                         fontWeight: '600',
//                         color: '#10b981',
//                         display: 'flex',
//                         alignItems: 'center',
//                         gap: '6px'
//                       }}>
//                         <Zap size={16} />
//                         Auto-Applied Offers ({autoAppliedOffers.length})
//                       </div>
//                       <div className="offers-grid">
//                         {autoAppliedOffers.map(offer => renderOfferCard(offer, true, true))}
//                       </div>
//                     </>
//                   )}
                  
//                   {/* ✅ Show manually applied offers separately */}
//                   {manuallyAppliedOffers.length > 0 && (
//                     <>
//                       <div style={{ 
//                         marginTop: autoAppliedOffers.length > 0 ? '24px' : '0',
//                         marginBottom: '12px', 
//                         fontSize: '14px', 
//                         fontWeight: '600',
//                         color: '#3b82f6'
//                       }}>
//                         Manually Applied Offers ({manuallyAppliedOffers.length})
//                       </div>
//                       <div className="offers-grid">
//                         {manuallyAppliedOffers.map(offer => renderOfferCard(offer, true, false))}
//                       </div>
//                     </>
//                   )}
//                 </>
//               )}
//             </div>
//           )}

//           {/* Available Offers Tab */}
//           {activeTab === 'available' && (
//             <div className="offers-section">
//               {availableOffers.length > 0 ? (
//                 <>
//                   <div className="section-header">
//                     <h3>🎯 Available Offers</h3>
//                   </div>
//                   <div className="offers-grid">
//                     {availableOffers.map(offer => renderOfferCard(offer))}
//                   </div>
//                 </>
//               ) : (
//                 <div className="empty-state">
//                   <Gift size={48} color="#ccc" />
//                   <p>No offers available for your cart</p>
//                   <span>Add more items to unlock offers</span>
//                 </div>
//               )}
//             </div>
//           )}

//           {/* Active Offers Tab */}
//           {activeTab === 'active' && (
//             <div className="offers-section">
//               {activeOffers.length === 0 ? (
//                 <div className="empty-state">
//                   <Tag size={48} color="#ccc" />
//                   <p>No active offers</p>
//                   <span>Check back later for new offers</span>
//                 </div>
//               ) : (
//                 <>
//                   <div className="section-header">
//                     <h3>🎉 All Active Offers</h3>
//                   </div>
//                   <div className="offers-grid">
//                     {activeOffers.map(offer => (
//                       <div key={offer.id} className="offer-card">
//                         <div className="offer-card-header">
//                           <div className="offer-icon" style={{ backgroundColor: '#f59e0b20', color: '#f59e0b' }}>
//                             <Tag size={20} />
//                           </div>
//                           <div className="offer-info">
//                             <h4 className="offer-name">{offer.name}</h4>
//                             <p className="offer-description">{offer.description}</p>
//                           </div>
//                         </div>

//                         {offer.coupon_code && (
//                           <div className="offer-code">
//                             <span>Code: <strong>{offer.coupon_code}</strong></span>
//                             <button 
//                               className="copy-code-btn"
//                               onClick={() => {
//                                 navigator.clipboard.writeText(offer.coupon_code);
//                                 onCouponChange(offer.coupon_code);
//                               }}
//                             >
//                               Copy
//                             </button>
//                           </div>
//                         )}

//                         <button 
//                           className="offer-apply-btn"
//                           onClick={() => {
//                             if (offer.coupon_code) {
//                               onCouponChange(offer.coupon_code);
//                               setTimeout(() => onApplyCoupon(), 100);
//                             }
//                           }}
//                           disabled={processing}
//                         >
//                           Apply This Offer
//                         </button>
//                       </div>
//                     ))}
//                   </div>
//                 </>
//               )}
//             </div>
//           )}
//         </div>
//       </div>

     
// {notification && (
//   <div className="offer-notification-overlay" onClick={() => setNotification(null)}>
//     <div className="offer-notification-container" onClick={(e) => e.stopPropagation()}>
//       {/* Success Circle */}
//       {notification.type === 'success' && (
//         <div className="offer-notification-success-circle">
//           <CheckCircle size={48} color="#16a34a" />
//         </div>
//       )}
      
//       {/* Error Circle */}
//       {notification.type === 'error' && (
//         <div className="offer-notification-error-circle">
//           <XCircle size={48} color="#ef4444" />
//         </div>
//       )}

//       {/* Title */}
//       <h2 className="offer-notification-title">{notification.title}</h2>

//       {/* Offer Details Box (for success with discount) */}
//       {notification.offerDetails && (
//         <div className="offer-notification-details-box">
//           <span className="offer-notification-label">You Saved:</span>
//           <span className="offer-notification-value">
//             ₹{notification.offerDetails.discount.toFixed(2)}
//           </span>
//         </div>
//       )}

//       {/* Subtitle/Message */}
//       <p className="offer-notification-subtitle">{notification.message}</p>

//       {/* Auto dismiss countdown */}
//       <div className="offer-notification-countdown">
//         Closing in {countdown}s
//       </div>
//     </div>
//   </div>
// )}
//     </div>
//   );
// };

// export default OffersModal;